import { useQuery } from '@apollo/client'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { GetCustomerAddressesDocument } from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { NonEmptyCartContext } from '../../../lib/cart/context'
import {
  setUpdatePaymentDetailsInput,
  updatePaymentDetailsAndSetPlaceOrderTask$,
} from '../../../lib/cart/state/payment'
import { ValidFormValues } from '../../../lib/cart/types'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { usePageUrl } from '../../../lib/pages'
import { logAndCaptureException } from '../../../utils/errorTools'
import NoPaymentDetailsRequired from './NoPaymentDetailsRequired'

const NoPaymentDetailsRequiredContainer: React.FC = () => {
  const customerCart = useContext(NonEmptyCartContext)
  const token = useContext(TokenContext)
  const dispensary = useDispensaryContext()
  const [error, setError] = useState<Error | undefined>(undefined)
  const [submitting, setSubmitting] = useState(false)
  const { navigate } = usePageUrl()

  const {
    data: customerAddressesData,
    loading: customerAddressesLoading,
    error: customerAddressesError,
  } = useQuery(GetCustomerAddressesDocument, {
    context: { token },
  })

  useLogAndCaptureError(customerAddressesError)

  const onSubmit = (values: ValidFormValues.NoPaymentDetailsRequired) => {
    if (customerAddressesLoading) {
      logAndCaptureException(new Error('Customer addresses not loaded before onSubmit'))
      return
    }

    setError(undefined)
    setSubmitting(true)

    setUpdatePaymentDetailsInput({
      fieldValues: values,
      customerAddresses: customerAddressesData?.customer?.addresses,
      customerCart,
      dispensarySlug: dispensary?.slug ?? '',
    })
  }

  useEffect(() => {
    const subscription = updatePaymentDetailsAndSetPlaceOrderTask$.subscribe((result) => {
      if (result._tag === 'Left') {
        // log payment, server, and application errors and capture in Sentry
        logAndCaptureException(result.error)
        setError(result.error)
        setSubmitting(false)
      } else {
        navigate('/checkout/review')
      }
    })
    return () => subscription.unsubscribe()
  }, [navigate])

  const defaultBillingAddress = useMemo(() => {
    const addresses = customerAddressesData?.customer?.addresses
    return addresses ? addresses.find((address) => address?.default_billing) : undefined
  }, [customerAddressesData?.customer?.addresses])

  return (
    <NoPaymentDetailsRequired
      cart={customerCart}
      defaultBillingAddress={defaultBillingAddress}
      error={error}
      onSubmit={onSubmit}
      submitting={submitting}
    />
  )
}

export default NoPaymentDetailsRequiredContainer
